"use client";
import React from "react";

import { SvgIconProps } from "../Icon/Icon";
import { Icon, IconNameType } from ".";
import { AllEncompassingIconNameType, loadIcon } from "./IconImports";

export const DEFAULT_PRECACHED_ICONS: IconNameType[] = [
  "arrow-down",
  "arrow-left",
  "arrow-right",
  "arrow-up",
  "caret-down",
  "caret-left",
  "caret-right",
  "caret-up",
  "check",
  "check-circle",
  "info",
  "link",
  "magnifying-glass",
  "share-fat",
  "warning-octagon",
  "x-circle",
  "x"
];

const PreCachedIcons = ({
  icons
}: {
  icons: AllEncompassingIconNameType[];
}) => {
  // precaching icons avoids flickering when icons are loaded for the first time
  return (
    <div style={{ display: "none" }}>
      {icons.map((icon) => (
        <Icon key={"precached-" + icon} icon={icon as IconNameType} />
      ))}
    </div>
  );
};

const defaultContext = {
  cacheIconComponent: () => () => null,
  getIconComponent: () => () => null,
  iconCacheRef: new Map()
};

interface IconContext {
  getIconComponent: (
    iconName: AllEncompassingIconNameType
  ) => React.FC<SvgIconProps>;
  cacheIconComponent: (
    iconName: AllEncompassingIconNameType,
    LazyComponent: React.FC<{}>
  ) => void;
  iconCacheRef: Map<AllEncompassingIconNameType, React.FC>;
}

export const IconContext = React.createContext<IconContext>({
  ...defaultContext
});

export const IconProvider: React.FC<
  React.PropsWithChildren<{
    preCacheIcons: IconNameType[]; // we only support precaching for regular icons
  }>
> = ({ preCacheIcons, children }) => {
  const iconCacheRef = React.useRef<Map<AllEncompassingIconNameType, React.FC>>(
    new Map()
  );

  const mergedPreCacheIcons = React.useMemo(() => {
    return Array.from(new Set([...DEFAULT_PRECACHED_ICONS, ...preCacheIcons]));
  }, [preCacheIcons]);

  const getIconComponent = React.useCallback(
    (iconName: AllEncompassingIconNameType): React.FC<SvgIconProps> => {
      if (iconCacheRef.current.has(iconName)) {
        return iconCacheRef.current.get(iconName)!;
      }

      const LazyComponent = loadIcon(iconName);

      const IconComponent: React.FC = (props) => (
        <React.Suspense>
          <LazyComponent {...props} />
        </React.Suspense>
      );

      IconComponent.displayName = iconName;

      return IconComponent;
    },
    []
  );

  const cacheIconComponent = React.useCallback(
    (iconName: AllEncompassingIconNameType, LazyComponent: React.FC<{}>) => {
      if (!iconCacheRef.current.has(iconName)) {
        iconCacheRef.current.set(iconName, LazyComponent);
      }
    },
    []
  );

  const contextValue = React.useMemo(
    () => ({
      getIconComponent,
      cacheIconComponent,
      iconCacheRef: iconCacheRef.current
    }),
    [getIconComponent, cacheIconComponent]
  );

  return (
    <IconContext.Provider value={contextValue}>
      <PreCachedIcons icons={mergedPreCacheIcons} />
      {children}
    </IconContext.Provider>
  );
};

export const useIconCache = () => {
  const context = React.useContext(IconContext);
  if (!context) {
    throw new Error("useIconCache must be used within an IconCacheProvider");
  }
  return context;
};
